import { useEffect } from "react";
import { Quill } from "react-quill";
import { IFRAME_CLASS, IFRAME_WRAP_CLASS } from "../blots/IframeBlot";

const TOOLBAR_CLASS = "ffair-iframe-toolbar";
const REMOVE_BTN_CLASS = "ffair-iframe-toolbar-remove";
const STYLE_TAG_ID = "ffair-iframe-toolbar-style";

type Args = {
  rootEditorElement: HTMLElement | null;
  editorRef: React.MutableRefObject<any>;
  contentVersion: number;
};

const ensureStyles = () => {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  // Hidden by default everywhere; only the editor surface (.ql-editor) opts
  // in to showing it on hover. CSS :hover propagates to the wrap even when
  // the iframe is the visual top layer, so we don't need to disable iframe
  // pointer events.
  style.textContent = `
    .${TOOLBAR_CLASS} { display: none; }
    .ql-editor .${IFRAME_WRAP_CLASS}:hover > .${TOOLBAR_CLASS} { display: flex; }
  `;
  document.head.appendChild(style);
};

const buildToolbar = (): HTMLDivElement => {
  const toolbar = document.createElement("div");
  toolbar.className = TOOLBAR_CLASS;
  toolbar.setAttribute("contenteditable", "false");
  toolbar.style.cssText = [
    "position:absolute",
    "top:0",
    "left:0",
    "right:0",
    "box-sizing:border-box",
    "padding:8px 12px",
    "justify-content:space-between",
    "align-items:center",
    "background:rgba(255,255,255,0.5)",
    "font-family:inherit",
    "font-size:12px",
    "font-weight:500",
    "line-height:1.2",
    "letter-spacing:1.2px",
    "color:#2979FF",
    "text-transform:uppercase",
    "z-index:5",
    "user-select:none",
    // Background pill is click-through so the iframe stays interactive.
    "pointer-events:none",
    "backdrop-filter:blur(2px)",
  ].join(";");
  toolbar.innerHTML = `
    <span style="line-height:1.2;">iFrame</span>
    <button type="button" class="${REMOVE_BTN_CLASS}" aria-label="Remove iframe" style="
      display:inline-flex;
      align-items:center;
      justify-content:center;
      width:24px;
      height:24px;
      padding:0;
      border:0;
      background:#2979FF;
      cursor:pointer;
      border-radius:50%;
      color:#FFFFFF;
      pointer-events:auto;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 18 18" fill="none">
        <path fill="currentColor" d="M14.25 4.808 13.193 3.75 9 7.943 4.808 3.75 3.75 4.808 7.943 9 3.75 13.193l1.058 1.057L9 10.057l4.193 4.193 1.057-1.057L10.057 9z"/>
      </svg>
    </button>
  `;
  return toolbar;
};

const removeToolbars = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLDivElement>(`.${TOOLBAR_CLASS}`)
    .forEach((node) => node.remove());
};

const clearLeakedPointerEvents = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLIFrameElement>(`iframe.${IFRAME_CLASS}`)
    .forEach((node) => {
      // Remove pointer-events:none baked into the inline style by an older
      // version of this hook (otherwise the iframe stays uninteractive).
      if (node.style.pointerEvents === "none") {
        node.style.removeProperty("pointer-events");
      }
    });
};

export const useIframeHoverToolbar = ({
  rootEditorElement,
  editorRef,
  contentVersion,
}: Args): void => {
  useEffect(() => {
    if (!rootEditorElement) return undefined;
    ensureStyles();
    removeToolbars(rootEditorElement);
    clearLeakedPointerEvents(rootEditorElement);

    const cleanups: Array<() => void> = [];

    rootEditorElement
      .querySelectorAll<HTMLElement>(`.${IFRAME_WRAP_CLASS}`)
      .forEach((wrap) => {
        const toolbar = buildToolbar();
        wrap.appendChild(toolbar);
        const removeBtn = toolbar.querySelector<HTMLButtonElement>(
          `.${REMOVE_BTN_CLASS}`
        );

        const onRemove = (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          const quillEditor = editorRef.current?.getEditor();
          try {
            const blot = quillEditor ? Quill.find(wrap) : null;
            if (quillEditor && blot) {
              const index = quillEditor.getIndex(blot);
              quillEditor.deleteText(index, 1, "user");
            } else {
              wrap.remove();
            }
          } catch {
            wrap.remove();
          }
        };

        removeBtn?.addEventListener("mousedown", onRemove);

        cleanups.push(() => {
          removeBtn?.removeEventListener("mousedown", onRemove);
          toolbar.remove();
        });
      });

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [rootEditorElement, editorRef, contentVersion]);
};
